import { useContext, lazy, Suspense, useMemo, useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import Layout from "./pages/layout";
import AuthLayout from "./pages/authLayout";
import Login from "./pages/login/login-view";
import PageNotFound from "./helper/pageNotFound.js";
import ProtectedRoute from "./helper/context/auth/ProtectedRoute";
import { AuthContext } from "./helper/context/auth/authContext.jsx";
import { getUserPermissions } from "./helper/function-helper";
import InlineLoader from "./component/loader.js";
import { Box } from "@mui/material";
import LoginHistoryDataGrid from "./pages/personalization/profile.js";
import { useDynamicTab } from "./component/useDynamicLogo.js";
import { clubitsLogo } from "./helper/constant-values.js";



const Dashboard = lazy(() => import("./component/dashboard"));
const EmployeeDashboard = lazy(() => import("./component/employeeDashboard.js"));
const CreateUser = lazy(() => import("./pages/createUser"));
const ListEmployee = lazy(() => import("./pages/createUser/listEmployee"));
const CompanySetup = lazy(() => import("./pages/settings/companySetup"));
const ListCompanySetUp = lazy(() => import("./pages/settings/listCompanySetUp"));
const PersonalizationView = lazy(() => import("./pages/personalization/personalization"));
const LeaveManagement = lazy(() => import("./pages/leaves/leaveManagement.js"));
const AuthorizedTable = lazy(() => import("./pages/leaves/leaveAuthorized.js"));
const AttendanceInfo = lazy(() => import("./pages/attendance/AttendanceInfo.js"));
const ShiftManagement = lazy(() => import("./pages/attendance/shiftManagement.js"));
const AttendanceAuthorization = lazy(() => import("./pages/authorizationScreen/attendanceAuthorization.js"));
const PayRollInputs = lazy(() => import("./pages/payRoll/payroll-inputs/pay-roll-inputs.js"));
const PayrollProcess = lazy(() => import("./pages/payRoll/process-payroll/payroll-process.js"));
const PayrollInputsBulk = lazy(() => import("./pages/payRoll/payroll-inputs/bulk-Inputs.js"));
const SalaryStatement = lazy(() => import("./pages/payRoll/salaryStatement.js"));
const BankFile = lazy(() => import("./pages/payRoll/bankFile/bankFile.js"));
const PayrollLockScreen = lazy(() => import("./pages/payRoll/payroll-lockScreen/payrollLockScreen.js"));
const BulkPayslip = lazy(() => import("./pages/payRoll/bulkPaySlip.js"));
const FinalSettleMent = lazy(() => import("./pages/payRoll/finalSettlement.js/index.js"));
const CustomReport = lazy(() => import("./pages/payRoll/customReport.js"));
const SeparationEmployee = lazy(() => import("./pages/payRoll/employeeSeparation/separationEmployee.js"));
const PayElementTab = lazy(() => import("./pages/master/index.js"));
const PayGroup = lazy(() => import("./pages/master/payGroup.js"));
const CreatePayGroupToEmployee = lazy(() => import("./pages/processPeriodMaster/payGroupToEmployee.js"));
const ProcessPeriodTable = lazy(() => import("./pages/processPeriodMaster/processPeriodMaster.js"));
const PayElementGrouping = lazy(() => import("./pages/processPeriodMaster/payElementGrouping.js"));
const CalendarMaster = lazy(() => import("./pages/processPeriodMaster/calendarMaster.js"));
const QueryBuilder = lazy(() => import("./pages/queryBuilder/queryBuilder.js"));
const PayrollDashboard = lazy(() => import("./pages/payRoll/payrollDashbord/index.js"));
const HoldReleasePayroll = lazy(() => import("./pages/payRoll/holdReleasePayroll/index.js"));
const Workflow = lazy(() => import("./pages/settings/workflow/workflow.js"));
const CompanyAdditioinalSetup = lazy(() => import("./pages/settings/companyAdditionalSetup.js"));
const ToDoList = lazy(() => import("./pages/toDoList/index.js"));
const UserToEmployeeMapping = lazy(() => import("./pages/createUser/userToEmployee.js"));
const AirTicketEligibility = lazy(() => import("./pages/airTicket/eligibilityIndex.js"));
const AirTicketAmountBulk = lazy(() => import("./pages/airTicket/airFareIndex.js"));
const YTDReports = lazy(() => import("./pages/payRoll/ytdReports.js"));
const RuleConfig = lazy(() => import("./pages/ruleConfig/index.js"));
const TaxSlabForm = lazy(() => import("./pages/taxslab/index.js"));
const BulkUploadEmployee = lazy(() => import("./pages/bulkUpload/employeeDetails.js"));
const BulkUploadCompensation = lazy(() => import("./pages/bulkUpload/compensationDetails.js"));
const PayrollHub = lazy(() => import("./pages/payRoll/payrollHub/payroll-hub.js"));
const EmployeeAdditionalSection = lazy(() => import("./pages/settings/employeeAdditionlSection.js"));
const CoreHR = lazy(() => import("./pages/payRoll/coreHR/core-hr.js"));
const CostEmployee = lazy(() => import("./pages/payRoll/employeeCostCenter/costEmployee.js"));
const SecondaryMaster = lazy(() => import("./pages/settings/secondaryMaster/index.js"));
const IntegrationHub = lazy(() => import("./pages/settings/integrations/integration.js"));
const SsoLogin = lazy(() => import("./pages/login/SsoLogin.js"));
const LockScreensecondary = lazy(() => import("./pages/payRoll/payroll-lockScreen/secondaryLockScreenDashboard.js"));
const IssueList = lazy(() => import("./pages/helpdesk/IssueList.jsx"));
const EmployeeWorkflow = lazy(() => import("./pages/employeeWorkFlow/employee-Workflow.jsx"));
const HelpdeskDashboard = lazy(() => import("./pages/helpdesk/helpdeskDashboard.jsx"));
const ReportsMaster = lazy(() => import("./pages/reports/ReportMaster.jsx"));
const ReportsAccess = lazy(() => import("./pages/reports/ReportsAccess.jsx"));
const ReportsView = lazy(() => import("./pages/reports/ReportView.jsx"));
const PayrollCompliance = lazy(() => import("./pages/payrollCompliance/PayrollCompliance.jsx"));
const PayrollDetailsDashboard = lazy(() => import("./pages/payRoll/payrollDashbord/PayrollDetailsDashboard.jsx"));


function App() {
  const { user, loading } = useContext(AuthContext);
  const data = user?.[0] || {};
  const userPermissions = getUserPermissions();
  const hasAccess = useMemo(
    () => (moduleName, screenName) =>
      userPermissions?.some(
        (permission) =>
          permission.ModuleName === moduleName &&
          permission.ScreenName === screenName
      ),
    [userPermissions]
  );

const AccessWrapper = ({ hasAccess, element }) => {
  const [showLoader, setShowLoader] = useState(!hasAccess);

  useEffect(() => {
    let timer;
    if (!hasAccess) {
      timer = setTimeout(() => setShowLoader(false), 2000);
    }
    return () => clearTimeout(timer);
  }, [hasAccess]);

  if (hasAccess) return element;
  return showLoader ? (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        width: "100%",
      }}
    >
      <InlineLoader />
    </Box>
  ) : (
    <PageNotFound />
  );
}

  const filterDashboard = useMemo(
    () => userPermissions?.find((item) => item.ModuleName === "HRMS Dashboard"),
    [userPermissions]
  );

  const filterPayrollHub = useMemo(
    () => userPermissions?.find((item) => item.ScreenName === "Payroll Hub"),
    [userPermissions]
  );

  const isEmployeeDashboard = filterDashboard && data?.RoleName === "Employee";
  
  const filterSuperVisor = useMemo(
    () => userPermissions?.find((item) => item.ScreenName === "Employee Workflow"),
    [userPermissions]
  );

  const isSuperVisor =
  filterSuperVisor &&
  ["Supervisor"].includes(data?.RoleName);

  const getDashboardComponent = useMemo(() => {
    if (isSuperVisor) return <EmployeeWorkflow />;
    if (isEmployeeDashboard) return <EmployeeDashboard />;
    if (filterDashboard) return <Dashboard />;
    if (filterPayrollHub) return <PayrollHub />;
    return <PayrollLockScreen />;
  }, [isEmployeeDashboard, filterDashboard, filterPayrollHub, isSuperVisor]);
  const isLoggedIn = !!localStorage.getItem("token");

  useDynamicTab(
    isLoggedIn
      ? `${user?.[0]?.LocationName || 'HRMS'}`
      : "Login",
    `${user?.[0]?.CompanyLogo || clubitsLogo}`
  );

  if (loading) return <div>Loading...</div>;

    const protectedRoutes = [
    {
      path: "/",
      element: getDashboardComponent,
      access: isSuperVisor ? ["Employee", "Employee Workflow"] : filterDashboard
        ? ["HRMS Dashboard", "HRMS Dashboard"]
        : filterPayrollHub
        ? ["Payroll", "Payroll Hub"]
        : ["Payroll", "Payroll Authorization"],
    },
    { path: "/create-employee", element: <CreateUser />, access: ["Employee", "Employee Profile"] },
    { path: "/view-employee", element: <ListEmployee />, access: ["Employee", "Employee Profile"] },
    { path: "/company-setup", element: <CompanySetup />, access: ["Settings", "Company Setup"] },
    { path: "/company-AdditionalSetup", element: <CompanyAdditioinalSetup />, access: ["Settings", "Company Additional Parameters"] },
    { path: "/workflow", element: <Workflow />, access: ["Settings", "Workflow"] },
    { path: "/view-companySetup", element: <ListCompanySetUp />, access: ["Settings", "Company Setup"] },
    { path: "/secondary", element: <SecondaryMaster />, access: ["Settings", "Secondary Master"] },
    { path: "/core-hr", element: <CoreHR />, access: ["Employee", "Core HR"] },
    { path: "/personalization", element: <PersonalizationView /> },
    { path: "/login-activity", element: <LoginHistoryDataGrid /> },
    { path: "/leave", element: <LeaveManagement />, access: ["Leaves", "View Leaves"] },
    { path: "/leave-authorized", element: <AuthorizedTable />, access: ["Leaves", "Leave Authorized"] },
    { path: "/attendance-info", element: <AttendanceInfo /> },
    { path: "/attendance-Authorization", element: <AttendanceAuthorization />, access: ["Attendance", "Authorization"] },
    { path: "/shift", element: <ShiftManagement />, access: ["Attendance", "Shift Management"] },
    { path: "/payroll-inputs", element: <PayRollInputs />, access: ["Payroll", "Payroll Inputs"] },
    { path: "/payroll-hub", element: <PayrollHub />, access: ["Payroll", "Payroll Hub"] },
    { path: "/payroll-process", element: <PayrollProcess />, access: ["Payroll", "Processing"] },
    { path: "/pay-master", element: <PayElementTab />, access: ["Payroll", "Process period Master"] },
    { path: "/salary-statement", element: <SalaryStatement />, access: ["Payroll", "Salary Statement"] },
    { path: "/custom-report", element: <CustomReport />, access: ["Payroll", "Custom Report"] },
    { path: "/bulk-inputs", element: <PayrollInputsBulk />, access: ["Payroll", "Payroll Bulk Inputs"] },
    { path: "/bank-file", element: <BankFile />, access: ["Payroll", "Bank File"] },
    { path: "/hold-release-payroll", element: <HoldReleasePayroll />, access: ["Payroll", "Hold/Release Payroll Process"] },
    { path: "/employee-cost-center", element: <CostEmployee />, access: ["Payroll", "Employee cost center"] },
    { path: "/payroll-authorization", element: <PayrollLockScreen />, access: ["Payroll", "Payroll Authorization"] },
    { path: "/pay-slip", element: <BulkPayslip />, access: ["Payroll", "Pay Slip"] },
    { path: "/Payslip-Tracker", element: <LockScreensecondary />, access: ["Payroll", "Payroll Tracker"] },
    { path: "/final-settlement", element: <FinalSettleMent />, access: ["Payroll", "Final Settlement"] },
    { path: "/ytd-reports", element: <YTDReports />, access: ["Payroll", "YTD Reports"] },
    { path: "/pay-group-to-employee", element: <CreatePayGroupToEmployee />, access: ["Payroll", "Pay Group to Employee"] },
    { path: "/process-period-master", element: <ProcessPeriodTable />, access: ["Payroll", "Process period Master"] },
    { path: "/pay-element-group-mapping", element: <PayElementGrouping />, access: ["Payroll", "Pay Element Group Mapping"] },
    { path: "/calendar-master", element: <CalendarMaster />, access: ["Payroll", "Calendar Master"] },
    { path: "/separation-employee", element: <SeparationEmployee />, access: ["Employee", "Employee Separation"] },
    { path: "/query-builder", element: <QueryBuilder />, access: ["Query Builder", "Query Builder"] },
    { path: "/ticket-eligible", element: <AirTicketEligibility />, access: ["Employee", "Air ticket Eligibility"] },
    { path: "/ticket-fare", element: <AirTicketAmountBulk />, access: ["Employee", "Air Fare"] },
    { path: "/to-do", element: <ToDoList />, access: ["To Do", "To Do"] },
    { path: "/user-to-employee", element: <UserToEmployeeMapping />, access: ["Employee", "User to Employee"] },
    { path: "/rule-config", element: <RuleConfig />, access: ["Payroll", "Rule Config"] },
    { path: "/tax-slab", element: <TaxSlabForm />, access: ["Payroll", "Tax Slab"] },
    { path: "/employee-bulk-upload", element: <BulkUploadEmployee />, access: ["Employee", "Employee Bulk Upload"] },
    { path: "/Compensation-bulk-upload", element: <BulkUploadCompensation />, access: ["Employee", "Compensation Bulk Upload"] },
    { path: "/employee-AdditionalSection", element: <EmployeeAdditionalSection />, access: ["Settings", "Employee Additional section"] },
    { path: "/integrations", element: <IntegrationHub />, access: ["Integrations", "Integrations"] },
    { path: "/helpDesk", element: <IssueList />, access: ["Helpdesk", "Helpdesk Ticketing"] },
    { path: "/employee-workflow", element: <EmployeeWorkflow />, access: ["Employee", "Employee Workflow"] },
    { path: "/helpDesk-dashboard", element: <HelpdeskDashboard />, access: ["Helpdesk", "Helpdesk Dashboard"] },
    { path: "/reports-master", element: <ReportsMaster />, access: ["Reports", "Report Master"], },
    { path: "/reports-access", element: <ReportsAccess />, access: ["Reports", "Reports Access"], },
    { path: "/reports-view", element: <ReportsView />, access: ["Reports", "Report View"], },
    { path: "/payroll-compliance", element: <PayrollCompliance />, access: ["Payroll", "Payroll Compliance"] },
    { path: "/payroll-dashboard", element: <PayrollDetailsDashboard />, access: ["Payroll", "Payroll Dashboard"], },
    { path: "/404", element: <PageNotFound /> },
  ];
  return (
    <div className="App">
      {data && (
        <Router>
            <Routes>
              <Route element={<Layout />}>
                {protectedRoutes.map(({ path, element, access = null }) => (
                  <Route
                    key={path}
                    path={path}
                    element={
                      <ProtectedRoute
                          element={
                            <Suspense
                              fallback={
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    height: "100vh",
                                    width: "100%",
                                  }}
                                >
                                  <InlineLoader />
                                </Box>
                              }
                            >
                     
                          <AccessWrapper
                            hasAccess={!access || hasAccess(...access)}
                            element={element}
                          />
                        </Suspense>
                      }
                      role={data?.Role}
                    />
                  }
                />
              ))}
            </Route>
            <Route element={<AuthLayout />}>
              <Route
                path="/ssologin"
                element={
                  <Suspense
                    fallback={
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          height: "100vh",
                          width: "100%",
                        }}
                      >
                        <InlineLoader />
                      </Box>
                    }
                  >
                    <SsoLogin />
                  </Suspense>
                }
              />
              <Route path="/login" element={<Login />} />
            </Route>
          </Routes>
        </Router>
      )}
    </div>
  );
}

export default App;
